import Cleave from 'cleave.js';
import Notification from 'CoreComponents/notification/';
import { redirectTo } from 'Utils/window';

import PromotionCodeAdapter from '../../adapters/promotionCode';
import PaymentAdapter from 'Core/adapters/payment';
import * as customEvent from './events';
import * as $ from 'jquery';

// Default timetable form selector
const DEFAULT_SELECTOR = '.form-payment';

const SELECTOR = {
  storedCard: 'input[name="[payment][card_id]"]',
  // NOTE: Here, we're using `data-name` to prevent sending credit card information fields to the backend server via HTTP Post
  // (according to the security best practice https://www.omise.co/security-best-practices#never-send-card-data-through-your-servers).
  cardNumberSection: '.form-payment__card-number',
  cardHolderSection: '.form-payment__cardholder',
  expirationSection: '.form-payment__expiration-security',
  cardNumber: 'input[data-name="cardNumber"]',
  cardholder: 'input[data-name="cardholder"]',
  expiration: 'input[data-name="expiration"]',
  securityCode: 'input[data-name="securityCode"]',
  paymentProviderToken: 'input[name="[payment][provider_token]"]',
  inputPaymentMethod: 'input[name="[payment][payment_method]"]',
  inputPromotionRow: '.booking-promotion__line-item',
  inputPromotionCode: '.promotion_code',
  inputPromotionPrice: '.booking-promotion__input__price',
  inputPromotionSubmit: '.booking-promotion__submit',
  discountText: '.booking-promotion__line-discount',
  totalPrice: '.booking-summary__total_price'
};

const CLASS_NAME = {
  processing: 'form-payment--processing'
};

class PaymentForm {
  constructor(elementRef, options = {}) {
    this.elementRef = elementRef || document.querySelector(DEFAULT_SELECTOR);
    const selectorOptions = options['selector'] || {};

    // Stored cards
    this.storedCards = elementRef.querySelectorAll(selectorOptions['storedCard'] || SELECTOR['storedCard']);

    // New card details
    this.cardNumberSection = elementRef.querySelector(SELECTOR['cardNumberSection']);
    this.cardHolderSection = elementRef.querySelector(SELECTOR['cardHolderSection']);
    this.expirationSection = elementRef.querySelector(SELECTOR['expirationSection']);
    this.cardNumberInput = elementRef.querySelector(selectorOptions['cardNumber'] || SELECTOR['cardNumber']);
    this.cardholderInput = elementRef.querySelector(selectorOptions['cardholder'] || SELECTOR['cardholder']);
    this.expirationInput = elementRef.querySelector(selectorOptions['expiration'] || SELECTOR['expiration']);
    this.securityCodeInput = elementRef.querySelector(selectorOptions['securityCode'] || SELECTOR['securityCode']);
    this.paymentProviderTokenInput = elementRef.querySelector(selectorOptions['paymentProviderToken'] || SELECTOR['paymentProviderToken']);
    this.inputPromotionCode = elementRef.querySelector(SELECTOR['inputPromotionCode']);
    this.inputPaymentMethod = elementRef.querySelector(SELECTOR['inputPaymentMethod']);

    // Bind functions
    this.onSubmitHandler = this.onSubmitHandler.bind(this);
    this.onClickStoredCardHandler = this.onClickStoredCardHandler.bind(this);

    this.onProcessingHandler = this.onProcessingHandler.bind(this);
    this.onSuccessHandler = this.onSuccessHandler.bind(this);
    this.onFailureHandler = this.onFailureHandler.bind(this);

    this._setup();
    this._addEventListeners();
  }

  // Event Handlers

  /**
   * Event handler when the form is submitted.
   *
   * @param {Event} event - `submit` event.
   * */
  onSubmitHandler(event) {
    event.preventDefault();

    let inputPromotionCode = '';
    let promotionId = '';
    if (this.elementRef.querySelector(SELECTOR['inputPromotionCode'])) {
      inputPromotionCode = this.elementRef.querySelector(SELECTOR['inputPromotionCode']).value;
      promotionId = this.elementRef.querySelector('.booking_promotion_id').value;
    }

    if (promotionId.length === 0 && inputPromotionCode.length !== 0) {
      this._checkPromotionCode(event);

      return;
    }

    if ($('#creditcard__form').attr('style').length > 0) {
      $('#formPaymentInternetBanking').submit();
      return;
    }

    if (this._usingStoredCard()) {
      this._processPayment();
      return;
    }

    const formParams = this._serializedFormParams();
    const card = { /* eslint-disable camelcase */
      name: formParams.cardholder,
      number: formParams.cardNumber,
      expiration_month: formParams.expiration.split('/')[0],
      expiration_year: formParams.expiration.split('/')[1],
      security_code: formParams.securityCode
    };

    const createTokenHandler = (statusCode, response) => {
      if (statusCode === 200) {
        this.paymentProviderTokenInput.value = response.id;
        this._processPayment();
      } else {
        Notification.render('error', response.message);
      }
    };

    Omise.createToken('card', card, createTokenHandler);
  }

  /**
   * Event handler when a stored card is clicked.
   * Unselect the selected radio input when clicking on it a second time.
   *
   * @param {Event} event - `click` event.
   * */
  onClickStoredCardHandler(event) {
    const storeCardInput = event.currentTarget;
    if (storeCardInput.id === 'card-0') {
      this.cardNumberSection.style.display = 'block';
      this.cardHolderSection.style.display = 'block';
      this.expirationSection.style.display = 'flex';
    } else {
      this.cardNumberSection.style.display = 'none';
      this.cardHolderSection.style.display = 'none';
      this.expirationSection.style.display = 'none';
    }

    if (String(storeCardInput.dataset.checked) === 'true') {
      storeCardInput.checked = false;
      storeCardInput.dataset.checked = false;
    } else {
      storeCardInput.dataset.checked = true;

      // Reset any previously selected stored card
      this.storedCards
        .filter(storeCard => storeCard !== storeCardInput)
        .forEach(storeCard => {
          storeCard.dataset.checked = false;
        });
    }
  }

  // Custom Event Handlers

  /**
   * Event handler when the payment form is being processed
   *
   * @param {Event} _event - custom event
   * */
  onProcessingHandler(_event) {
    this._showSpinner();
  }

  /**
   * Event handler when the payment form is processed successfully
   * Redirect to the order detail page
   *
   * @param {Event} event - custom event
   * */
  onSuccessHandler(event) {
    redirectTo(event.detail.authorize_uri);
  }

  /**
   * Event handler when the payment form is NOT processed successfully
   *
   * @param {Event} event - custom event
   * */
  onFailureHandler(event) {
    const [error] = event.detail.errors || [];

    this._hideSpinner();
    Notification.render('error', error.detail);
  }

  // Private

  _setup() {
    // Setup input masks
    if (this.cardNumberInput && this.expirationInput) {
      new Cleave(this.cardNumberInput, { creditCard: true });
      new Cleave(this.expirationInput, { date: true, datePattern: ['m', 'y'] });
    }
  }

  _addEventListeners() {
    this.elementRef.addEventListener('submit', this.onSubmitHandler, false);
    this.storedCards.forEach(storedCard => storedCard.addEventListener('click', this.onClickStoredCardHandler));
    // Select default
    if (this.storedCards.length > 0) {
      this.storedCards[0].click();
    }

    this.elementRef.addEventListener(customEvent.PAYMENT_FORM_IS_PROCESSING, this.onProcessingHandler);
    this.elementRef.addEventListener(customEvent.PAYMENT_FORM_SUCCESS, this.onSuccessHandler);
    this.elementRef.addEventListener(customEvent.PAYMENT_FORM_FAILURE, this.onFailureHandler);
  }

  /**
   * Retrieve all input values.
   *
   * @return {Object} the form input values
   * */
  _serializedFormParams() {
    return {
      cardNumber: this.cardNumberInput.value,
      cardholder: this.cardholderInput.value,
      expiration: this.expirationInput.value || '',
      securityCode: this.securityCodeInput.value
    };
  }

  /**
   * Process the checkout payment.
   * */
  async _processPayment() {
    const processingEvent = new CustomEvent(customEvent.PAYMENT_FORM_IS_PROCESSING);
    this.elementRef.dispatchEvent(processingEvent);
    const actionUrl = this.elementRef.getAttribute('action');

    try {
      const paymentParams = {
        providerToken: this.paymentProviderTokenInput.value,
        cardId: this._usingStoredCard() && this._selectedStoredCard().value,
        paymentMethod: this.inputPaymentMethod.value
      };

      let promotionId = '';
      if (this.elementRef.querySelector('.booking_promotion_id')) {
        promotionId = this.elementRef.querySelector('.booking_promotion_id').value;
      }

      const authorizeUri = await PaymentAdapter.create(actionUrl, paymentParams, promotionId);

      const paymentSuccessEvent = new CustomEvent(customEvent.PAYMENT_FORM_SUCCESS, {
        detail: authorizeUri
      });
      this.elementRef.dispatchEvent(paymentSuccessEvent);
    } catch (error) {
      const paymentFailureEvent = new CustomEvent(customEvent.PAYMENT_FORM_FAILURE, {
        detail: error
      });
      this.elementRef.dispatchEvent(paymentFailureEvent);
    }
  }

  async _checkPromotionCode(event) {
    const inputPromotionCode = this.elementRef.querySelector(SELECTOR['inputPromotionCode']).value;
    const inputPromotionPrice = document.querySelector(SELECTOR['inputPromotionPrice']).value;
    const photographerId = document.querySelector('.booking-summary').getAttribute('data-photographer-id');
    const responsePromotionCode = await PromotionCodeAdapter.get(inputPromotionCode, inputPromotionPrice, photographerId);
    const input = this.elementRef.querySelector(SELECTOR['inputPromotionCode']);

    var valid = false;
    if (responsePromotionCode && responsePromotionCode.id) {
      this.elementRef.querySelector('.booking_promotion_id').value = responsePromotionCode.id;
      document.querySelectorAll(SELECTOR['discountText']).forEach((element) => {
        element.innerHTML = `<td><span style="color: #00ca9f">Discount</span></td>
                              <td><span style="color: #00ca9f">${responsePromotionCode.discount_price}</span></td>`;
      });
      input.className = 'booking-promotion__input__code';
      input.readOnly = true;
      valid = true;
    } else {
      document.querySelectorAll(SELECTOR['discountText']).forEach((element) => {
        element.innerHTML = '';
      });
      document.querySelectorAll(SELECTOR['discountText']).innerHTML = '';
      this.elementRef.querySelector('.booking_promotion_id').value = '';
      input.className = 'booking-promotion__input__code invalid';
      Notification.render('error', responsePromotionCode.message);
    }

    document.querySelector(SELECTOR['totalPrice']).innerHTML = `${responsePromotionCode.price}`;

    return valid;
  }

  // Private Methods
  _usingStoredCard() {
    return Boolean(this._selectedStoredCard());
  }

  _selectedStoredCard() {
    const storeCard = this.elementRef.querySelector(`${SELECTOR['storedCard']}:checked`);
    if (storeCard && +storeCard.value !== 0) {
      return storeCard;
    } else {
      return null;
    }
  }

  _showSpinner() {
    this.elementRef.classList.add(CLASS_NAME['processing']);
  }

  _hideSpinner() {
    this.elementRef.classList.remove(CLASS_NAME['processing']);
  }
}

export default PaymentForm;



// WEBPACK FOOTER //
// ./app/assets/javascripts/components/paymentForm/index.js